import React, { useEffect, useMemo, useState } from 'react';
import '../styles/ExploreCauses.css';
import { FaSearch, FaLock, FaGlobe } from 'react-icons/fa';
import CauseDetail from '../components/CauseDetail';
import { categories } from '../data/categories';
import { causes as legacyCauses } from '../data/causes';
import environment from '../config/environment';

const SALESFORCE_DONATE_BASE =
environment.SF_DONATION_BASE;

const normalize = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const buildLegacyLookup = () => {
  const map = new Map();

  legacyCauses.forEach((cause) => {
    const keys = [
      normalize(cause.slug),
      normalize(cause.name),
      normalize(cause.id),
      normalize(cause.salesforceId)
    ].filter(Boolean);

    keys.forEach((key) => {
      if (!map.has(key)) {
        map.set(key, cause);
      }
    });
  });

  return map;
};

const splitMultiValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .toString()
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
};

const ensureUrl = (value) => {
  if (!value) return '';
  const trimmed = value.toString().trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const ExploreCauses = () => {
  const { apiUrl } = environment();

  const [causes, setCauses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCause, setSelectedCause] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const legacyLookup = useMemo(() => buildLegacyLookup(), []);

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(`${apiUrl}/api/salesforce-public-causes`, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load causes (${response.status})`);
        }

        const data = await response.json();
        const sfCauses = data?.data?.causes || [];

        const enriched = sfCauses.map((cause) => {
          const legacyMatch =
            legacyLookup.get(normalize(cause.slug)) ||
            legacyLookup.get(normalize(cause.projectName)) ||
            legacyLookup.get(normalize(cause.name)) ||
            null;

          const category =
            cause.category ||
            cause.Category__c ||
            '';

          const subcategory =
            cause.subcategory ||
            cause.Subcategory__c ||
            '';

          const categoryList = [
            ...splitMultiValue(category),
            ...splitMultiValue(subcategory)
          ];

          return {
            ...cause,
            projectName: cause.projectName || cause.Project_Name__c || cause.name || '',
            description: cause.description || cause.Description__c || '',
            shortDescription:
              cause.shortDescription ||
              cause.description ||
              legacyMatch?.shortDescription ||
              '',
            mission:
              cause.mission ||
              cause.Mission__c ||
              legacyMatch?.mission ||
              cause.description ||
              '',
            about:
              cause.about ||
              cause.About__c ||
              '',
            website: ensureUrl(
              cause.website ||
                cause.Website__c ||
                legacyMatch?.website ||
                ''
            ),
            contactEmail:
              cause.contactEmail ||
              cause.Contact_Email__c ||
              '',
            logoUrl: cause.logoUrl || cause.Logo_URL__c || '',
            heroUrl: cause.heroUrl || cause.Hero_URL__c || '',
            previewImage:
              cause.heroUrl ||
              cause.logoUrl ||
              cause.Hero_URL__c ||
              cause.Logo_URL__c ||
              legacyMatch?.image ||
              '',
            primaryColor:
              cause.primaryColor ||
              cause.Primary_Color__c ||
              '#0f172a',
            category,
            subcategory,
            categories: categoryList.length ? categoryList : legacyMatch?.categories || []
          };
        });

        setCauses(enriched);
      } catch (err) {
        console.error('Failed to load causes:', err);
        setError('Unable to load causes right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchCauses();
  }, [apiUrl, legacyLookup]);

  const filteredCauses = causes.filter((cause) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matchesCategory =
      selectedCategory === 'all' ||
      (cause.categories || []).some(
        (item) => normalize(item) === normalize(selectedCategory)
      );

    const haystack = [
      cause.projectName,
      cause.name,
      cause.shortDescription,
      cause.description,
      cause.mission,
      cause.category,
      cause.subcategory
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const matchesSearch =
      normalizedSearch === '' || haystack.includes(normalizedSearch);

    return matchesCategory && matchesSearch;
  });

  const openCause = (cause) => {
    setSelectedCause(cause);
    document.body.style.overflow = 'hidden';
  };

  const closeCause = () => {
    setSelectedCause(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="explore-wrapper">
      <div className="explore-container">
        <div className="explore-header">
          <h1>Explore Our Causes</h1>
          <p>
            Discover projects, learn their mission, and support the work that matters to you.
          </p>

          <div className="search-section">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, mission, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="categories">
           

            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="featured-section">
          <h2>Current Causes</h2>

          {loading ? (
            <div className="loading-state">Loading causes...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button
                type="button"
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : filteredCauses.length === 0 ? (
            <div className="no-causes-message">
              <p>No causes matched your search or selected category.</p>
            </div>
          ) : (
            <div className="causes-grid--enhanced">
              {filteredCauses.map((cause) => {
                const donateUrl = `${SALESFORCE_DONATE_BASE}${encodeURIComponent(
                  cause.slug || ''
                )}`;

                return (
                  <article
                    key={cause.slug || cause.widgetId || cause.projectName}
                    className="cause-card--enhanced"
                    onClick={() => openCause(cause)}
                  >
                    <div className="cause-card-media">
  {cause.previewImage ? (
    <img
      src={cause.previewImage}
      alt={cause.projectName}
      className={`cause-card-image ${
        !cause.heroUrl && cause.logoUrl ? 'cause-card-image--logo' : ''
      }`}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  ) : (
    <div
      className="cause-card-image cause-card-image--fallback"
      style={{ background: cause.primaryColor }}
    >
      {cause.projectName?.charAt(0)}
    </div>
  )}
</div>

                    <div className="cause-card-body">
                      <span className="cause-card-label">Verified Cause</span>

                      <h3>{cause.projectName}</h3>

                      <p>
                        {(cause.shortDescription || cause.mission || 'Mission details coming soon.')
                          .trim()
                          .slice(0, 120)}
                        {(cause.shortDescription || cause.mission || '').length > 120 ? '…' : ''}
                      </p>
                    </div>

                    <div className="cause-card-actions">
                      <button
                        type="button"
                        className="cause-action-button cause-action-button--primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCause(cause);
                        }}
                      >
                        Learn More
                      </button>

                      <a
                        href={donateUrl}
                        className="cause-action-button cause-action-button--secondary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaLock size={12} /> Donate
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="start-cause-section">
          <div className="start-cause-content">
            <h2>Ready to Make Your Impact?</h2>
            <p>
              Have a charitable initiative you want to launch? Apply for fiscal sponsorship
              and build your mission with compliant infrastructure behind you.
            </p>

            <div className="cta-buttons">
              <a href="/apply" className="cta-button primary">
                Start Your Cause
              </a>
              <a href="/learn-more" className="cta-button secondary">
                Learn More
              </a>
            </div>
          </div>
        </div>

        {selectedCause && <CauseDetail cause={selectedCause} onClose={closeCause} />}
      </div>
    </div>
  );
};

export default ExploreCauses;